import * as React from "react"
import { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react"
import { cn } from "@/lib/utils"
import { BubbleMenu } from "../../editor/BubbleMenu"
import { handlePaste, escapeHtml, type PasteMode } from "@/lib/utils/pasteHandler"
import { applyFormatting, processHTMLContent } from "@/lib/utils/textFormatting"
import { useTextHighlightManager } from "@/components/editor/hooks/useTextHighlightManager"
import { useVariableReferenceManager } from "@/components/editor/hooks/useVariableReferenceManager"
import { VariableRefSuggestion } from "@/components/editor/VariableRefSuggestion"
import type { TypeItem } from "@/lib/stores/typesStore"
import type { ObjectItem } from "@/lib/stores/objects/types"
import { cleanVariableTokens } from "@/lib/utils/variableReferences"

interface TextAreaElementProps extends Omit<React.ComponentProps<"div">, "onChange" | "onBlur"> {
  /** HTML string when in edit mode; raw text/HTML shown literally in raw view */
  value?: string
  onChange?: (value: string) => void
  onBlur?: (value: string) => void
  placeholder?: string
  className?: string
  rows?: number
  /** If true, the content is not editable and formatting is hidden */
  disabled?: boolean
  /**
   * Rendering mode when not editing:
   *  - "view-formatted": render HTML (dangerouslySetInnerHTML)
   *  - "view-raw": render the literal symbols/markup (escaped)
   * Default is "view-formatted".
   */
  viewMode?: "view-formatted" | "view-raw"
  /**
   * How to handle paste:
   *  - "plain": paste as plain text (default) to avoid huge HTML payloads from apps like Figma/Docs
   *  - "basic-html": allow a minimal HTML subset via sanitizer
   */
  pasteMode?: PasteMode
  /** Enable text highlighting functionality */
  enableHighlighting?: boolean
  /** Enable bubble menu for text formatting. Default is true */
  enableBubbleMenu?: boolean
  /** Enable inline variable references ({{ key.name }} autocomplete and highlighting) */
  enableVariableReferences?: boolean
  /** Available TypeItems for variable reference resolution (required when enableVariableReferences is true) */
  variableTypes?: TypeItem[]
  /** Available ObjectItems for variable reference resolution (required when enableVariableReferences is true) */
  variableObjects?: ObjectItem[]
}



const TextAreaElement = React.forwardRef<HTMLDivElement, TextAreaElementProps>(
  (
    {
      value = "",
      onChange,
      onBlur,
      placeholder = "Enter text...",
      className,
      disabled = false,
      viewMode = "view-formatted",
      pasteMode = "plain",
      enableHighlighting = false,
      enableBubbleMenu = true,
      enableVariableReferences = false,
      variableTypes = [],
      variableObjects = [],
      rows = 4,
      ...props
    },
    ref
  ) => {
    const [bubbleMenu, setBubbleMenu] = useState({
      isVisible: false,
      position: { x: 0, y: 0 },
      selectedText: "",
    })

    const editableRef = useRef<HTMLDivElement>(null)
    const savedSelectionRef = useRef<Range | null>(null)
    
  // Highlighting functionality
  const {
    applyManualHighlight,
    removeHighlight,
    processTextWithHighlights,
    stripExistingHighlights,
    highlightItems
  } = useTextHighlightManager()

  // Variable reference functionality
  const {
    autocomplete: varRefAutocomplete,
    suggestions: varRefSuggestions,
    closeAutocomplete: closeVarRefAutocomplete,
    handleInputForVarRef,
    insertReference,
    applyDecoration: applyVarRefDecoration,
    stripReferenceDecoration: stripVarRefDecoration,
  } = useVariableReferenceManager({
    types: enableVariableReferences ? variableTypes : [],
    objects: enableVariableReferences ? variableObjects : [],
  })

  // Auto-apply highlights when content changes and element is not focused
  const applyAutomaticHighlights = useCallback(() => {
    if (!enableHighlighting || !editableRef.current) return;
    
    try {
      // Store original HTML to compare
      const originalHTML = editableRef.current.innerHTML;
      
      // Process text with highlights
      const result = processTextWithHighlights(editableRef.current);
      
      // Update the element with highlighted content
      if (result.highlightedHTML !== originalHTML) {
        editableRef.current.innerHTML = result.highlightedHTML;
        
        // Trigger onChange with the highlighted content
        onChange?.(result.highlightedHTML);
      }
    } catch (error) {
      console.error('Error applying automatic highlights:', error);
    }
  }, [enableHighlighting, processTextWithHighlights, onChange])


  // Apply highlights when highlight items change (only if not currently focused)
  useEffect(() => {
    if (enableHighlighting && editableRef.current && highlightItems.length > 0) {
      // Check if the element is currently focused
      const isActive = document.activeElement === editableRef.current;
      
      // Only auto-highlight if not currently editing
      if (!isActive) {
        applyAutomaticHighlights();
      }
    }
  }, [highlightItems, enableHighlighting, applyAutomaticHighlights])

  // Expose the inner div via the forwarded ref
  useEffect(() => {
    if (!ref) return
    if (typeof ref === "function") {
      ref(editableRef.current)
    } else {
      ; (ref as React.MutableRefObject<HTMLDivElement | null>).current = editableRef.current
    }
  }, [ref])

  // Calculate min height based on rows (1.5rem per row)
  const minHeightRem = rows * 1

  // Auto-grow helper (defined early so it can be used in other hooks)
  // Tracks the last applied height to avoid redundant DOM writes that cause
  // DevTools element flashing and unnecessary layout recalculations.
  const lastHeightRef = useRef<number | null>(null)

  const autoGrow = useCallback(() => {
    const el = editableRef.current
    if (!el) return

    // Quick check: if scrollHeight matches the current height, no resize needed.
    // This avoids the "auto" → height cycle that causes DevTools element flashing.
    const currentHeight = el.offsetHeight
    if (currentHeight > 0 && el.scrollHeight <= currentHeight && lastHeightRef.current === currentHeight) {
      return
    }

    // Temporarily reset height to measure natural content height
    el.style.height = "auto"
    const newHeight = el.scrollHeight
    el.style.height = `${newHeight}px`
    lastHeightRef.current = newHeight
  }, [])

  // Track the last value that was synced to innerHTML to avoid redundant writes.
  // Without this, browser HTML normalization can cause processHTMLContent output
  // to differ from editableRef.current.innerHTML, triggering repeated writes.
  const lastSyncedValueRef = useRef<string>(value)

  // Keep innerHTML in sync while editing
  useEffect(() => {
    if (!editableRef.current) return
    // Skip if the value hasn't changed since our last sync
    if (value === lastSyncedValueRef.current && editableRef.current.innerHTML !== '') return

    // Clean any corrupted variable tokens (e.g. highlight spans inside {{ }})
    const cleanedValue = enableVariableReferences ? cleanVariableTokens(value) : value
    // Process the content to ensure proper text flow
    const processedContent = processHTMLContent(cleanedValue);

    // Only write to DOM if the processed content actually differs from current innerHTML
    if (editableRef.current.innerHTML !== processedContent) {
      editableRef.current.innerHTML = processedContent;

      // Apply variable reference decoration for visual display
      if (enableVariableReferences) {
        applyVarRefDecoration(editableRef.current)
      }

      // After external value changes, ensure size is correct
      autoGrow()
    }

    lastSyncedValueRef.current = value
  }, [value, autoGrow, enableVariableReferences, applyVarRefDecoration])

  // Selection bubble (only when editable and bubble menu is enabled)
  useEffect(() => {
    if (disabled || !enableBubbleMenu) return
    const handleSelectionChange = () => {
      const selection = window.getSelection()
      if (!selection || selection.isCollapsed || !editableRef.current?.contains(selection.anchorNode)) {
        setBubbleMenu({ isVisible: false, position: { x: 0, y: 0 }, selectedText: "" })
        savedSelectionRef.current = null
        return
      }
      const range = selection.getRangeAt(0)
      const rect = range.getBoundingClientRect()
      const selectedText = selection.toString()
      
      // Save the selection range for later restoration
      savedSelectionRef.current = range.cloneRange()
      
      setBubbleMenu({
        isVisible: true,
        position: { x: rect.left + rect.width / 2 - 60, y: rect.top - 50 },
        selectedText,
      })
    }
    document.addEventListener("selectionchange", handleSelectionChange)
    return () => document.removeEventListener("selectionchange", handleSelectionChange)
  }, [disabled, enableBubbleMenu])

  // Auto-grow on mount & on window resize
  useLayoutEffect(() => {
    autoGrow()
    const onResize = () => autoGrow()
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [autoGrow])

    const handleInput = () => {
      if (!editableRef.current) return
      // Strip any decoration before saving so only clean {{ }} tokens are persisted
      let newContent = editableRef.current.innerHTML
      if (enableVariableReferences) {
        const clean = stripVarRefDecoration(newContent)
        if (clean !== newContent) {
          editableRef.current.innerHTML = clean
          newContent = clean
        }
      }
      onChange?.(newContent)
      autoGrow()

      // Detect {{ trigger for variable reference autocomplete
      if (enableVariableReferences) {
        handleInputForVarRef(editableRef.current)
      }
    }

  const handleBlur = () => {
    if (!editableRef.current) return
    
    // Apply automatic highlights when losing focus
    if (enableHighlighting) {
      applyAutomaticHighlights();
    }

    // Strip any decoration before saving, then re-decorate visually
    if (enableVariableReferences) {
      closeVarRefAutocomplete()
      // Strip decoration to get clean content for saving
      const currentHTML = editableRef.current.innerHTML
      const clean = stripVarRefDecoration(currentHTML)
      if (clean !== currentHTML) {
        editableRef.current.innerHTML = clean
      }
    }
    
    const newContent = editableRef.current.innerHTML
    onBlur?.(newContent)

    // Re-apply visual decoration after saving clean content
    if (enableVariableReferences && editableRef.current) {
      applyVarRefDecoration(editableRef.current)
    }
  }

  const handleFormat = useCallback((command: string, value?: string) => {
    if (!editableRef.current) return;

    // Restore the saved selection before applying formatting
    if (savedSelectionRef.current) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedSelectionRef.current);
      }
    }

    // Focus the editable element to ensure formatting applies
    editableRef.current.focus();

    // Handle highlighting commands if enabled
    if (enableHighlighting) {
      if (command === 'highlight' && value) {
        try {
          const highlightData = JSON.parse(value);
          applyManualHighlight(highlightData, bubbleMenu.selectedText);
          
          // Update content with highlights
          if (editableRef.current) {
            onChange?.(editableRef.current.innerHTML);
          }
          
          // Hide the bubble menu
          setBubbleMenu({ isVisible: false, position: { x: 0, y: 0 }, selectedText: "" });
          autoGrow();
          return;
        } catch (error) {
          console.error('Error applying highlight:', error);
        }
      } else if (command === 'manual-highlight' && value) {
        try {
          const data = JSON.parse(value);
          applyManualHighlight(data, bubbleMenu.selectedText);
          
          // Update content with highlights
          if (editableRef.current) {
            onChange?.(editableRef.current.innerHTML);
          }
          
          // Hide the bubble menu
          setBubbleMenu({ isVisible: false, position: { x: 0, y: 0 }, selectedText: "" });
          autoGrow();
          return;
        } catch (error) {
          console.error('Error applying manual highlight:', error);
        }
      } else if (command === 'manual-unhighlight' || command === 'remove-highlight') {
        try {
          removeHighlight(bubbleMenu.selectedText);
          
          // Update content
          if (editableRef.current) {
            onChange?.(editableRef.current.innerHTML);
          }
          
          // Hide the bubble menu
          setBubbleMenu({ isVisible: false, position: { x: 0, y: 0 }, selectedText: "" });
          autoGrow();
          return;
        } catch (error) {
          console.error('Error removing highlight:', error);
        }
      }
    }

    // Use the shared formatting utility for other commands
    const normalizedContent = applyFormatting(command, value, editableRef.current);
    onChange?.(normalizedContent);

    // Hide the bubble menu after formatting
    setBubbleMenu({ isVisible: false, position: { x: 0, y: 0 }, selectedText: "" });
    
    // Keep focus on the editable element
    editableRef.current.focus();
    
    autoGrow();
  }, [enableHighlighting, applyManualHighlight, removeHighlight, bubbleMenu.selectedText, onChange, autoGrow]);

  // Non-edit mode: either show formatted HTML or literal symbols/markup
  if (disabled) {
    const baseClasses = cn(
      "block w-full text-base ring-offset-background md:text-sm",
      // No user resizing, wrap long words, preserve line breaks
      "overflow-visible whitespace-pre-wrap break-words",
      // Add formatting support for HTML elements using Tailwind arbitrary variants
      "[&_strong]:font-bold [&_b]:font-bold",
      "[&_em]:italic [&_i]:italic",
      "[&_u]:underline",
      "[&_s]:line-through [&_strike]:line-through",
      "[&_ul]:list-disc [&_ul]:ml-6 [&_ul]:my-2",
      "[&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:my-2",
      "[&_li]:my-1",
      "[&_h1]:text-3xl [&_h1]:font-bold [&_h1]:my-2",
      "[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:my-2",
      "[&_h3]:text-xl [&_h3]:font-bold [&_h3]:my-2",
      "[&_h4]:text-lg [&_h4]:font-bold [&_h4]:my-1",
      "[&_h5]:text-base [&_h5]:font-bold [&_h5]:my-1",
      "[&_h6]:text-sm [&_h6]:font-bold [&_h6]:my-1",
      // Custom class for additional styling
      "textarea-view-mode",
      className
    )

    if (viewMode === "view-formatted") {
      return (
        <>
          <style>{`
            .textarea-view-mode b,
            .textarea-view-mode strong {
              font-weight: 700 !important;
            }
            .textarea-view-mode i,
            .textarea-view-mode em {
              font-style: italic !important;
            }
            .textarea-view-mode u {
              text-decoration: underline !important;
            }
            .textarea-view-mode s,
            .textarea-view-mode strike {
              text-decoration: line-through !important;
            }
            .textarea-view-mode ul {
              list-style-type: disc !important;
              margin-left: 1.5rem !important;
              margin-top: 0.5rem !important;
              margin-bottom: 0.5rem !important;
            }
            .textarea-view-mode ol {
              list-style-type: decimal !important;
              margin-left: 1.5rem !important;
              margin-top: 0.5rem !important;
              margin-bottom: 0.5rem !important;
            }
            .textarea-view-mode li {
              margin-top: 0.25rem !important;
              margin-bottom: 0.25rem !important;
            }
            .textarea-view-mode h1 {
              font-size: 1.875rem !important;
              font-weight: 700 !important;
              margin-top: 0.5rem !important;
              margin-bottom: 0.5rem !important;
            }
            .textarea-view-mode h2 {
              font-size: 1.5rem !important;
              font-weight: 700 !important;
              margin-top: 0.5rem !important;
              margin-bottom: 0.5rem !important;
            }
            .textarea-view-mode h3 {
              font-size: 1.25rem !important;
              font-weight: 700 !important;
              margin-top: 0.5rem !important;
              margin-bottom: 0.5rem !important;
            }
            .textarea-view-mode h4 {
              font-size: 1.125rem !important;
              font-weight: 700 !important;
              margin-top: 0.25rem !important;
              margin-bottom: 0.25rem !important;
            }
            .textarea-view-mode h5 {
              font-size: 1rem !important;
              font-weight: 700 !important;
              margin-top: 0.25rem !important;
              margin-bottom: 0.25rem !important;
            }
            .textarea-view-mode h6 {
              font-size: 0.875rem !important;
              font-weight: 700 !important;
              margin-top: 0.25rem !important;
              margin-bottom: 0.25rem !important;
            }
            /* Highlight styles for view mode */
            .textarea-view-mode .text-highlight {
              background-color: hsl(var(--primary) / 0.15) !important;
              border-radius: 2px !important;
              padding: 1px 2px !important;
              cursor: default !important;
              transition: background-color 0.2s ease !important;
            }
            .textarea-view-mode .text-highlight:hover {
              background-color: hsl(var(--primary) / 0.25) !important;
            }
            /* Variable reference styles for view mode */
            .textarea-view-mode .var-ref {
              display: inline !important;
              background-color: hsl(220 90% 56% / 0.12) !important;
              color: hsl(220 90% 40%) !important;
              border: 1px solid hsl(220 90% 56% / 0.25) !important;
              border-radius: 4px !important;
              padding: 0 4px !important;
              font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace !important;
              font-size: 0.85em !important;
              white-space: nowrap !important;
              cursor: default !important;
            }
            .textarea-view-mode .var-ref:hover {
              background-color: hsl(220 90% 56% / 0.2) !important;
              border-color: hsl(220 90% 56% / 0.4) !important;
            }
            .textarea-view-mode .var-ref-unresolved {
              background-color: hsl(0 80% 56% / 0.1) !important;
              color: hsl(0 80% 40%) !important;
              border-color: hsl(0 80% 56% / 0.25) !important;
            }
          `}</style>
          <div
            aria-readonly
            className={baseClasses}
            style={{ minHeight: `${minHeightRem}rem` }}
            // We *intentionally* render HTML when explicitly asked
            dangerouslySetInnerHTML={{ __html: value || "" }}
            {...props}
          />
        </>
      )
    }

    // Default: show raw content so users see symbols/markup literally
    return (
      <div
        aria-readonly
        className={baseClasses}
        style={{ minHeight: `${minHeightRem}rem` }}
        // Render escaped HTML as text, not interpreted
        dangerouslySetInnerHTML={{ __html: value || "" }}
        {...props}
      />
    ) 
  }

    // Handle paste to strip heavy HTML (e.g., Figma, Google Docs)
    const onPaste: React.ClipboardEventHandler<HTMLDivElement> = (e) => {
      if (!editableRef.current) return
      handlePaste(e, pasteMode, undefined, autoGrow)
    }

  // Edit mode
  return (
    <div className="relative">
      <style>{`
        /* Highlight styles for edit mode */
        .text-highlight {
          background-color: hsl(var(--primary) / 0.15);
          border-radius: 2px;
          padding: 1px 2px;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        .text-highlight:hover {
          background-color: hsl(var(--primary) / 0.25);
        }
        /* Variable reference styles */
        .var-ref {
          display: inline;
          background-color: hsl(220 90% 56% / 0.12);
          color: hsl(220 90% 40%);
          border: 1px solid hsl(220 90% 56% / 0.25);
          border-radius: 4px;
          padding: 0 4px;
          font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace;
          font-size: 0.85em;
          white-space: nowrap;
          cursor: default;
          transition: background-color 0.15s ease, border-color 0.15s ease;
        }
        .var-ref:hover {
          background-color: hsl(220 90% 56% / 0.2);
          border-color: hsl(220 90% 56% / 0.4);
        }
        .var-ref-unresolved {
          background-color: hsl(0 80% 56% / 0.1);
          color: hsl(0 80% 40%);
          border-color: hsl(0 80% 56% / 0.25);
        }
        .var-ref-unresolved:hover {
          background-color: hsl(0 80% 56% / 0.18);
          border-color: hsl(0 80% 56% / 0.4);
        }
        /* Dark mode adjustments */
        @media (prefers-color-scheme: dark) {
          .var-ref {
            background-color: hsl(220 90% 60% / 0.15);
            color: hsl(220 90% 75%);
            border-color: hsl(220 90% 60% / 0.3);
          }
          .var-ref:hover {
            background-color: hsl(220 90% 60% / 0.25);
          }
          .var-ref-unresolved {
            background-color: hsl(0 80% 60% / 0.12);
            color: hsl(0 80% 70%);
            border-color: hsl(0 80% 60% / 0.25);
          }
        }
      `}</style>
      <div
        ref={editableRef}
        contentEditable
        dir="auto"
        onInput={handleInput}
        onBlur={handleBlur}
        onPaste={onPaste}
        data-placeholder={placeholder}
        className={cn(
          // Base
          "flex w-full border border-input bg-background text-foreground px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm whitespace-pre-wrap break-words leading-relaxed",
          // Auto-grow behavior: no manual resize, no scrollbars
          "resize-none overflow-hidden",
          // Placeholder when empty
          "empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground empty:before:pointer-events-none",
          className
        )}
        style={{
          minHeight: `${minHeightRem}rem`,
          height: "auto",
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }}
        {...props}
      />

      {enableBubbleMenu && (
        <BubbleMenu 
          isVisible={bubbleMenu.isVisible} 
          position={bubbleMenu.position} 
          selectedText={bubbleMenu.selectedText}
          onFormat={handleFormat} 
        />
      )}

      {enableVariableReferences && (
        <VariableRefSuggestion
          isOpen={varRefAutocomplete.isOpen}
          position={varRefAutocomplete.position}
          items={varRefSuggestions}
          onSelect={(refString) => {
            if (editableRef.current) {
              insertReference(refString, editableRef.current)
              // Save clean content (with raw {{ }} tokens, no decoration markup)
              const cleanHTML = stripVarRefDecoration(editableRef.current.innerHTML)
              onChange?.(cleanHTML)
            }
          }}
          onClose={closeVarRefAutocomplete}
        />
      )}
    </div>
  )
  }
)

TextAreaElement.displayName = "TextAreaElement"



export { TextAreaElement }
